/**
 * SkillDelivery Platform
 * InviteWelcomeLight -- R14 Round 1 progressive-disclosure view.
 *
 * Mounted when the server sets `data.page.layout = 'light'` (either
 * via `INVITE_PAGE_LAYOUT=light` on Render or a preview token with
 * `?layout=light`).
 *
 * Section order (same as classic for muscle memory):
 *   Overview -> Roadmap -> What we can help with -> Research -> Feedback.
 *
 * What's different from classic:
 *   - Hero line is a single sentence; no personal_lines wall.
 *   - Roadmap steps collapse to just title + 1-line why. "See details"
 *     reveals bullets + how_we_help.
 *   - Offering cards collapse to name + status pill + one-liner.
 *     "Open" reveals details + pilot question + pilot button.
 *   - Feedback shows step thumbs + CTA buttons + privacy at first
 *     paint; the two text boxes sit behind "Tell us more".
 *
 * Media (loops, posters, lightbox) ship in a later round behind
 * INVITE_MEDIA_ENABLED. Round 1 is layout + disclosure only.
 *
 * Preview mode (page.preview === true) expands every section by
 * default so the owner can proof the full page before send.
 *
 * STATE: this file is a self-contained page -- same fetches as
 * classic (so cold refresh works). Reuses `useInviteFeedback` + a
 * handful of leaf components from InviteWelcome.classic.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import PreviewShell from '../components/invite/PreviewShell';
import {
  API_BASE, APP_BASE, PENDING_INVITE_KEY,
  SKD_CYAN, SKD_CYAN_DARK, SKD_CTA_GRADIENT,
  Loading, NotFound, Expired,
  SectionHeading, QuickActionButton, ReactionChip,
  GradientBanner, OwnerCard,
  useInviteFeedback
} from './InviteWelcome.classic';
import Turnstile from '../components/security/Turnstile';

export default function InviteWelcomeLight({ initialState, initialContent } = {}) {
  const { token } = useParams();
  // R14 Round 2 fix 1 -- dispatcher owns the fetch. Fallback shape
  // identical to classic so a stand-alone mount still works.
  const [state, setState] = useState(() => initialState || { loading: true });
  const [content, setContent] = useState(() => initialContent || null);
  const skipFetch = !!initialState;

  useEffect(() => {
    if (initialState) setState(initialState);
  }, [initialState]);
  useEffect(() => {
    if (initialContent) setContent(initialContent);
  }, [initialContent]);

  useEffect(() => {
    if (skipFetch) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}`, { cache: 'no-store' });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setState({ loading: false, expired: true, owner: body.owner || null });
          return;
        }
        if (res.status === 404 || !res.ok || !body.success) {
          setState({ loading: false, notFound: true });
          return;
        }
        setState({ loading: false, page: body.data.page, meta: body.data.meta });
      } catch (_) {
        if (!cancelled) setState({ loading: false, notFound: true });
      }
    })();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}/content`, {
          headers: { Accept: 'application/json' }
        });
        if (res.status === 204) return;
        const body = await res.json().catch(() => ({}));
        if (!cancelled && body && body.success) setContent(body.data || null);
      } catch (_) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [token, skipFetch]);

  const recordClick = useCallback(async (itemType, itemId) => {
    try {
      await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_type: itemType, item_id: itemId || null })
      });
    } catch (_) { /* best-effort */ }
  }, [token]);

  // Hooks that must run on every render (keep ABOVE the early
  // returns -- react-hooks/rules-of-hooks).
  const _page    = state && state.page;
  const _org     = (_page && _page.contact && (_page.contact.organisation || _page.contact.first_name)) || 'your team';
  const _ownerFirst = (_page && _page.owner && _page.owner.full_name
    ? _page.owner.full_name.split(/\s+/)[0]
    : null) || 'the sender';
  const _ownerFullName = (_page && _page.owner && _page.owner.full_name) || _ownerFirst;
  const _roadmap = _page && _page.sections && _page.sections.roadmap;
  const _isPreview = !!(_page && _page.preview === true);

  const feedback = useInviteFeedback({
    token, isPreview: _isPreview, content, ownerFirst: _ownerFirst, roadmap: _roadmap
  });

  const topBannerText = (content && content.welcome && content.welcome.top_banner)
    || `You are previewing ${_org}'s workspace, prepared by SkillDelivery AI · shared by ${_ownerFullName}`;

  useEffect(() => {
    if (typeof document !== 'undefined' && _page) document.title = topBannerText;
  }, [topBannerText, _page]);

  if (state.loading) {
    return <PreviewShell brand={{}} organisation=""><Loading /></PreviewShell>;
  }
  if (state.notFound) {
    return <PreviewShell brand={{}} organisation=""><NotFound /></PreviewShell>;
  }
  if (state.expired) {
    return (
      <PreviewShell brand={{}} organisation="">
        <Expired owner={state.owner} />
      </PreviewShell>
    );
  }

  const { page, meta } = state;
  const brand = page.brand || {};
  const brandColor = brand.color || '#0891b2';
  const org = _org;
  const ownerFirst = _ownerFirst;
  const insights = page.sections?.insights || [];
  const research = page.sections?.research || [];
  const roadmap = _roadmap;
  const isPreview = _isPreview;

  const topStrip = (
    <>
      <span>{topBannerText}</span>
      {isPreview ? (
        <span
          aria-disabled="true"
          title="Disabled in preview"
          style={{
            background: '#e2e8f0', color: '#64748b', textDecoration: 'none',
            padding: '6px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            cursor: 'not-allowed'
          }}
        >
          Disabled in preview
        </span>
      ) : (
        <a
          href={`${APP_BASE}/register`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            try {
              localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({
                token, expiresAt: meta?.expires_at || null
              }));
            } catch (_) {}
            recordClick('cta', null);
          }}
          style={{
            background: SKD_CYAN, color: 'white', textDecoration: 'none',
            padding: '6px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 6
          }}
        >
          Make this my workspace <ExternalLink size={13} />
        </a>
      )}
    </>
  );

  return (
    <PreviewShell brand={brand} organisation={org} ownerName={page.owner?.full_name} topStrip={topStrip}>
      {page.preview && (
        <div style={{
          marginBottom: 20, padding: '10px 14px', borderRadius: 10,
          background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
          fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span style={{
            background: '#92400e', color: '#fef3c7',
            padding: '2px 8px', borderRadius: 999, fontSize: 11, letterSpacing: '0.04em'
          }}>Preview</span>
          Owner preview — nothing on this page is live. Links, tracking and sign-up are disabled.
        </div>
      )}

      {/* 1. Overview -- single hero line, no personal_lines wall. */}
      <section id="overview" style={{ scrollMarginTop: 20 }}>
        <GradientBanner
          brandColor={brandColor}
          organisation={org}
          firstName={page.contact?.first_name}
          personal={null /* light: hero stays short; personal lines live inside the roadmap outcome card */}
          entityType={page.contact?.represents}
          domain={page.contact?.domain}
          industryLabel={page.contact?.industry_label || null}
        />
        <OwnerCard
          owner={page.owner}
          meetingLabel={page.event_label}
          shareUrl={typeof window !== 'undefined' ? window.location.href : ''}
        />
      </section>

      {/* 2. Roadmap -- steps collapse to title + why. "See details"
          reveals bullets + how_we_help. First step auto-expanded on
          mobile; preview mode expands everything. */}
      <LightRoadmapSection
        roadmap={roadmap}
        isPreview={isPreview}
        brandColor={brandColor}
        personalLines={page.sections?.personal}
      />

      {/* 3. What we can help with -- offering cards collapse to
          name + status pill + one-liner. Tap opens: details + pilot
          question + pilot button. */}
      <LightOfferingsSection fb={feedback} isPreview={isPreview} />

      {/* 4. Research / insights -- unchanged shape; classic cards. */}
      {(insights.length > 0 || research.length > 0) && (
        <section id="research" style={{ marginTop: 36, scrollMarginTop: 20 }}>
          {insights.length > 0 && (
            <>
              <SectionHeading title="Market insights" />
              <LightCardGrid items={insights} onClick={(id) => recordClick('insight', id)} />
            </>
          )}
          {research.length > 0 && (
            <div style={{ marginTop: insights.length > 0 ? 24 : 0 }}>
              <SectionHeading title="Research & domain knowledge" />
              <LightCardGrid items={research} onClick={(id) => recordClick('research', id)} />
            </div>
          )}
        </section>
      )}

      {/* 5. Feedback -- thumbs + CTAs + privacy at first paint. The
          two textareas sit behind "Tell us more". */}
      <LightFeedbackSection fb={feedback} />

      {page.cta?.show && (
        <section style={{
          marginTop: 40, padding: 24, borderRadius: 12,
          background: SKD_CTA_GRADIENT, color: 'white', textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px', color: 'white', fontSize: 20 }}>Make this your workspace</h3>
          <p style={{ margin: '0 0 16px', opacity: 0.9, fontSize: 14 }}>
            Create a free account to explore everything above.
          </p>
          {isPreview ? (
            <span
              aria-disabled="true"
              style={{
                display: 'inline-block', padding: '12px 24px', borderRadius: 8,
                background: 'rgba(255,255,255,0.4)', color: 'white',
                fontWeight: 700, fontSize: 15, cursor: 'not-allowed'
              }}
            >Disabled in preview</span>
          ) : (
            <a
              href={`${APP_BASE}/register`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                try {
                  localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({
                    token, expiresAt: meta?.expires_at || null
                  }));
                } catch (_) {}
                recordClick('cta', null);
              }}
              style={{
                display: 'inline-block', padding: '12px 24px', borderRadius: 8,
                background: 'white', color: SKD_CYAN_DARK,
                fontWeight: 700, textDecoration: 'none', fontSize: 15
              }}
            >{page.cta?.label || 'Create your free account'}</a>
          )}
        </section>
      )}

      <footer style={{
        marginTop: 32, paddingTop: 16, borderTop: '1px solid #e2e8f0',
        color: '#64748b', fontSize: 13
      }}>
        {page.owner?.full_name && (
          <p style={{ margin: '0 0 6px' }}>
            Sent by <strong style={{ color: '#334155' }}>{page.owner.full_name}</strong> on SkillDelivery
          </p>
        )}
        {page.footer?.opt_out_line && <p style={{ margin: '0 0 6px' }}>{page.footer.opt_out_line}</p>}
        {page.footer?.erase_url && (
          <p style={{ margin: 0 }}>
            <a href={page.footer.erase_url} style={{ color: '#64748b' }}>Remove my details</a>
          </p>
        )}
      </footer>
    </PreviewShell>
  );
}

// ─── Light roadmap section ─────────────────────────────────────────────────
//
// Each step chip renders title + 1-line why. Bullets + how_we_help sit
// behind a per-step "See details" toggle. First step auto-expanded on
// mobile; preview mode expands everything.
function LightRoadmapSection({ roadmap, isPreview, brandColor, personalLines }) {
  const stepsSource = (roadmap && roadmap.display && Array.isArray(roadmap.display.steps))
    ? roadmap.display.steps
    : (roadmap && Array.isArray(roadmap.steps) ? roadmap.steps : null);
  const steps = Array.isArray(stepsSource) ? stepsSource : [];
  const [open, setOpen] = useState(() => {
    // First step open by default when NOT in preview; preview opens all.
    const o = {};
    if (!steps.length) return o;
    if (isPreview) for (let i = 0; i < steps.length; i++) o[i] = true;
    else o[0] = true;
    return o;
  });
  if (!steps.length) return null;

  return (
    <section id="roadmap" style={{ marginTop: 36, scrollMarginTop: 20 }}>
      <SectionHeading title="Your roadmap" />
      <div style={{ display: 'grid', gap: 10 }}>
        {steps.map((s, i) => {
          const title = s && (s.name || s.title || s.step) ? (s.name || s.title || s.step) : `Step ${i + 1}`;
          const why = s && s.why ? s.why : null;
          const bullets = s && Array.isArray(s.bullets) ? s.bullets : [];
          const howWeHelp = s && s.how_we_help ? s.how_we_help : null;
          const isOpen = !!open[i];
          return (
            <div key={i} style={{
              padding: 14, borderRadius: 10,
              background: '#ffffff', border: `1px solid ${i === 0 ? brandColor + '40' : '#e2e8f0'}`
            }}>
              <button
                type="button"
                onClick={() => setOpen({ ...open, [i]: !isOpen })}
                aria-expanded={isOpen}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: i === 0 ? brandColor : '#e2e8f0',
                  color: i === 0 ? 'white' : '#334155',
                  fontSize: 12, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}>{i + 1}</span>
                <span style={{ flex: 1, color: '#0f172a', fontSize: 15, fontWeight: 600 }}>{title}</span>
                {isOpen ? <ChevronDown size={16} color="#64748b" /> : <ChevronRight size={16} color="#64748b" />}
              </button>
              {why && (
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 6, fontStyle: 'italic', paddingLeft: 32 }}>
                  {why}
                </div>
              )}
              {isOpen && (
                <div style={{ paddingLeft: 32, marginTop: 10 }}>
                  {bullets.length > 0 && (
                    <ul style={{ margin: '0 0 10px', paddingLeft: 18, color: '#334155', fontSize: 14 }}>
                      {bullets.map((b, bi) => (<li key={bi} style={{ margin: '4px 0' }}>{b}</li>))}
                    </ul>
                  )}
                  {howWeHelp && (
                    <div style={{
                      marginTop: 8, padding: '8px 10px', borderRadius: 8,
                      background: '#eff6ff', border: '1px solid #bfdbfe',
                      color: '#1d4ed8', fontSize: 13
                    }}>
                      <strong style={{ fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        How SkillDelivery helps
                      </strong>
                      <div style={{ marginTop: 4 }}>{howWeHelp}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {personalLines && (
        <details style={{ marginTop: 16, color: '#334155', fontSize: 13 }}>
          <summary style={{ cursor: 'pointer', color: '#64748b' }}>Why I shared this</summary>
          <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{personalLines}</p>
        </details>
      )}
    </section>
  );
}

// ─── Light offerings section ───────────────────────────────────────────────
//
// Each card is name + status pill + one-liner by default. "Open"
// reveals details + pilot question + pilot button.
function LightOfferingsSection({ fb, isPreview }) {
  const { offerings, submitted, busy, submitQuickAction, content, label } = fb;
  const [open, setOpen] = useState(() => (isPreview && offerings ? Object.fromEntries(offerings.map((o) => [o.id, true])) : {}));
  if (submitted || !offerings.length) return null;
  const pilotLabel = (content && content.pilot_cta_label) || label('pilot_cta_fallback', 'Join the pilot');

  return (
    <section id="help" style={{ marginTop: 36, scrollMarginTop: 20 }}>
      <SectionHeading title="What we can help with" />
      <div style={{ display: 'grid', gap: 10 }}>
        {offerings.map((o) => {
          const isOpen = !!open[o.id];
          return (
            <div key={o.id} style={{
              padding: 14, borderRadius: 10,
              background: '#f8fafc', border: '1px solid #e2e8f0'
            }}>
              <button
                type="button"
                onClick={() => setOpen({ ...open, [o.id]: !isOpen })}
                aria-expanded={isOpen}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  gap: 10, width: '100%',
                  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  {isOpen ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#64748b" />}
                  <strong style={{ color: '#0f172a', fontSize: 15 }}>{o.name}</strong>
                </span>
                {o.status_line && (
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 999,
                    background: o.status === 'live' ? '#dcfce7'
                              : o.status === 'coming_soon' ? '#fef3c7' : '#e0e7ff',
                    color:      o.status === 'live' ? '#166534'
                              : o.status === 'coming_soon' ? '#92400e' : '#3730a3',
                    whiteSpace: 'nowrap'
                  }}>{o.status_line}</span>
                )}
              </button>
              {o.one_liner && !isOpen && (
                <div style={{ color: '#475569', fontSize: 13, marginTop: 6, paddingLeft: 22 }}>
                  {o.one_liner}
                </div>
              )}
              {isOpen && (
                <div style={{ marginTop: 10 }}>
                  {o.details && (
                    <div style={{ color: '#475569', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                      {o.details}
                    </div>
                  )}
                  {o.pilot_question_line && (
                    <div style={{
                      marginTop: 10, padding: '8px 10px', borderRadius: 8,
                      background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: 13
                    }}>{o.pilot_question_line}</div>
                  )}
                  {o.programme && (
                    <div style={{ marginTop: 10 }}>
                      <QuickActionButton
                        onClick={() => submitQuickAction('pilot_interest', {
                          offering_id: o.id, programme_id: o.programme.id
                        })}
                        disabled={isPreview || busy}
                        label={pilotLabel}
                        variant="brand"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Light feedback section ────────────────────────────────────────────────
//
// Thumbs row + CTAs + privacy at first paint. Textareas + Turnstile sit
// behind "Tell us more". Preview mode expands "Tell us more" by default
// so the owner can proof the whole form.
function LightFeedbackSection({ fb }) {
  const {
    submitted, busy, note, perStep, tsRef, tsError, captchaActive,
    goals, improve, wrongForm, dbCtas, steps,
    ownerFirst, isPreview,
    setGoals, setImprove, setWrongForm, setTsToken, setTsError, setNote,
    openWrongPerson, cancelWrongPerson,
    submitStepReaction, submitQuickAction, submitWrongPerson, submitTextareas,
    label
  } = fb;
  const [tellMore, setTellMore] = useState(!!isPreview);

  if (submitted) {
    return (
      <section style={{
        marginTop: 36, padding: 20, borderRadius: 12,
        background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1'
      }}>
        <strong>{note || label('thank_you', `Thanks — ${ownerFirst} will get back to you within 2 working days.`)}</strong>
      </section>
    );
  }

  return (
    <section style={{ marginTop: 36 }}>
      <SectionHeading title={label('feedback_heading', 'Was this helpful?')} />

      {/* Step thumbs row -- always visible. */}
      {steps.length > 0 && (
        <div style={{ marginBottom: 20, display: 'grid', gap: 8 }}>
          <div style={{ color: '#64748b', fontSize: 13 }}>React to any step:</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {steps.map((s, i) => {
              const title = (s && (s.name || s.title || s.step || `Step ${i + 1}`)) || `Step ${i + 1}`;
              const picked = perStep[i];
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: 8,
                  background: '#f8fafc', border: '1px solid #e2e8f0'
                }}>
                  <span style={{ flex: 1, color: '#334155', fontSize: 14 }}>{title}</span>
                  <ReactionChip
                    label={label('reaction.useful', 'Useful')}
                    active={picked === 'useful'}
                    onClick={() => submitStepReaction(i, 1)}
                    disabled={!!picked || isPreview || busy}
                  />
                  <ReactionChip
                    label={label('reaction.not_quite', 'Not quite')}
                    active={picked === 'not_quite'}
                    onClick={() => submitStepReaction(i, -1)}
                    disabled={!!picked || isPreview || busy}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA buttons from connect_ctas (request_call / send_details /
          link). pilot_interest NEVER renders here; wrong_person +
          not_interested are the privacy row below. */}
      {dbCtas.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {dbCtas
            .filter((cta) => cta && cta.action && cta.action !== 'pilot_interest')
            .map((cta) => {
              const feedbackType =
                cta.action === 'request_call' ? 'request_call'
                : cta.action === 'send_details' ? 'send_more_details'
                : null;
              if (cta.action !== 'link' && !feedbackType) return null;
              const onClick = () => {
                if (cta.action === 'link' && cta.link_url) {
                  window.open(cta.link_url, '_blank', 'noopener,noreferrer');
                  return;
                }
                return submitQuickAction(feedbackType, {});
              };
              return (
                <QuickActionButton
                  key={cta.id}
                  onClick={onClick}
                  disabled={isPreview || busy}
                  label={cta.label}
                />
              );
            })}
        </div>
      )}

      {/* Privacy row -- always visible, locked labels. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        <QuickActionButton
          onClick={openWrongPerson}
          disabled={isPreview || busy}
          label={label('privacy.wrong_person', 'Not the right person')}
          variant="muted"
        />
        <QuickActionButton
          onClick={() => submitQuickAction('not_interested', {})}
          disabled={isPreview || busy}
          label={label('privacy.not_interested', 'Not interested')}
          variant="muted"
        />
      </div>

      {wrongForm && (
        <div style={{
          marginTop: 12, padding: 12, borderRadius: 10,
          background: '#fffbea', border: '1px solid #fde68a'
        }}>
          <div style={{ color: '#92400e', fontSize: 13, marginBottom: 8 }}>
            Who is the right person? Share any of these — the owner reads this.
          </div>
          <input placeholder="Name" value={wrongForm.name}
            onChange={(e) => setWrongForm({ ...wrongForm, name: e.target.value })}
            style={_inpStyle} maxLength={120} />
          <input placeholder="Email" value={wrongForm.email}
            onChange={(e) => setWrongForm({ ...wrongForm, email: e.target.value })}
            style={_inpStyle} maxLength={240} type="email" />
          <input placeholder="Phone (incl. country code)" value={wrongForm.phone}
            onChange={(e) => setWrongForm({ ...wrongForm, phone: e.target.value })}
            style={_inpStyle} maxLength={32} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={submitWrongPerson} disabled={busy}
              style={{ padding: '6px 14px', borderRadius: 6, background: '#92400e', color: 'white', border: 'none', fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
              Send
            </button>
            <button type="button" onClick={cancelWrongPerson} disabled={busy}
              style={{ padding: '6px 14px', borderRadius: 6, background: 'white', color: '#92400e', border: '1px solid #92400e', fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tell us more -- reveals textareas + Turnstile. */}
      <button
        type="button"
        onClick={() => setTellMore(!tellMore)}
        aria-expanded={tellMore}
        style={{
          marginTop: 14, padding: '6px 12px', borderRadius: 8,
          background: 'transparent', color: '#334155',
          border: '1px dashed #cbd5e1',
          fontSize: 13, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6
        }}
      >
        {tellMore ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {tellMore ? 'Hide' : 'Tell us more'}
      </button>

      {tellMore && (
        <div style={{ marginTop: 14 }}>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <div style={{ color: '#334155', fontSize: 13, marginBottom: 6 }}>
              {label('challenges_prompt', "What's the one thing you're trying to get right in the next 60-90 days?")}
            </div>
            <textarea
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              maxLength={600}
              rows={2}
              disabled={isPreview}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <div style={{ color: '#334155', fontSize: 13, marginBottom: 6 }}>
              {label('improve_prompt', 'What would make this more useful?')}
            </div>
            <textarea
              value={improve}
              onChange={(e) => setImprove(e.target.value)}
              maxLength={600}
              rows={2}
              disabled={isPreview}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </label>

          {(goals.trim() || improve.trim()) && !isPreview && (
            <button
              type="button"
              onClick={submitTextareas}
              disabled={busy}
              style={{
                marginBottom: 16, padding: '8px 16px', borderRadius: 8,
                background: '#0891b2', color: 'white', border: 'none',
                fontSize: 14, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer'
              }}
            >
              Send
            </button>
          )}

          {captchaActive && (
            <div style={{ marginTop: 16 }}>
              <Turnstile
                ref={tsRef}
                onVerify={(t) => { setTsToken(t); setTsError(false); setNote(''); }}
                onExpire={() => { setTsToken(null); setNote('Security check expired — please tick the box again.'); }}
                onError={() => { setTsError(true); setNote('Could not load the security check. Please reload the page.'); }}
                size="normal"
                action="connect-feedback"
              />
              {tsError && (
                <div style={{ color: '#b45309', fontSize: 12 }}>
                  If this persists, your browser may be blocking Cloudflare's challenge.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {note && !submitted && (
        <div style={{ marginTop: 12, color: '#b45309', fontSize: 13 }}>{note}</div>
      )}

      <div style={{ color: '#64748b', fontSize: 12, marginTop: 16 }}>
        {label('closing_note', `Your note goes directly to ${ownerFirst}, who usually replies within 2 working days.`)}
      </div>
    </section>
  );
}

// Thin research/insight grid. Same shape as classic CardGrid but we
// drop the "AI/grow/resources" sections entirely (R13 Step 8 §2).
function LightCardGrid({ items, onClick }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {items.map((it) => (
        <a
          key={it.id}
          href={it.link_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!it.link_url) e.preventDefault();
            if (onClick) onClick(it.id);
          }}
          style={{
            display: 'block', padding: 12, borderRadius: 10,
            background: '#ffffff', border: '1px solid #e2e8f0',
            color: '#0f172a', textDecoration: 'none'
          }}
        >
          <strong style={{ fontSize: 14, color: '#0f172a' }}>{it.title || it.name}</strong>
          {(it.one_liner || it.source) && (
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
              {it.one_liner || it.source}
            </div>
          )}
        </a>
      ))}
    </div>
  );
}

const _inpStyle = {
  display: 'block', width: '100%', marginBottom: 6,
  padding: '6px 10px', borderRadius: 6, border: '1px solid #fde68a',
  fontSize: 13, fontFamily: 'inherit'
};
